import React, { useState, useEffect } from "react";
import "./style.css";

// MUI imports
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";

import {
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";

const GILROY_MEDIUM = "gilroyMedium";
const GILROY_BOLD = "GilroyBold";

// ---------- helpers ----------
const normalize = (text) =>
  (text ?? "").toString().replace(/\s+/g, " ").trim().toLowerCase();

const clean = (s) =>
  normalize(s)
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDash = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "-" : v;

const firstDisplayValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "-";
};

const formatMatchingType = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized || normalized === "-") return "-";

  return normalized
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const pick = (obj, keys) => {
  for (const k of keys) {
    if (
      obj &&
      obj[k] !== undefined &&
      obj[k] !== null &&
      String(obj[k]).trim() !== ""
    ) {
      return obj[k];
    }
  }
  return undefined;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const getLineItemCount = (documents) =>
  asArray(documents).reduce(
    (count, document) =>
      count + (Array.isArray(document?.line_items) ? document.line_items.length : 0),
    0
  );

const parseMoney = (s) => {
  if (s === null || s === undefined) return null;
  if (typeof s === "number") return Number.isFinite(s) ? s : null;

  const str = String(s).trim();
  if (!str) return null;

  // Keep only digits, minus and dot so we can parse values like:
  // "â‚¹4,89,000", "489,000.00", "â‚¹12,31,850", etc.
  const normalized = str.replace(/[^\d.-]+/g, "");
  if (
    !normalized ||
    normalized === "-" ||
    normalized === "." ||
    normalized === "-."
  ) {
    return null;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const normalizeBackendStatus = (status) =>
  String(status || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const isSuccessfulBackendStatus = (status) => {
  if (status === true) return true;

  const normalized = normalizeBackendStatus(status);
  if (!normalized) return false;

  if (
    normalized.includes("NOT MATCHED") ||
    normalized.includes("UNMATCHED") ||
    normalized.includes("MISMATCH") ||
    normalized.includes("EXCEED") ||
    normalized.includes("REJECT") ||
    normalized.includes("FAILED") ||
    normalized.includes("EXCEPTION")
  ) {
    return false;
  }

  return (
    normalized === "MATCHED" ||
    normalized === "FULL MATCHED" ||
    normalized === "FULLMATCHED" ||
    normalized === "FULLY MATCHED" ||
    normalized === "FULLYMATCHED" ||
    normalized === "2 WAY MATCHED" ||
    normalized === "2WAY MATCHED" ||
    normalized === "TWO WAY MATCHED" ||
    normalized === "TWOWAY MATCHED" ||
    normalized.endsWith(" MATCHED") ||
    normalized.includes("ITEM MATCHED") ||
    normalized.includes("PRICE MATCHED") ||
    normalized.includes("AMOUNT MATCHED") ||
    normalized.includes("QUANTITY MATCHED") ||
    ["TRUE", "COMPLETED", "APPROVED", "SUCCESS", "SUCCESSFUL"].includes(
      normalized
    )
  );
};

const toTitleStatus = (status) => {
  if (status === true) return "Matched";
  if (status === false) return "Not Matched";

  const normalized = normalizeBackendStatus(status);
  if (!normalized) return "No Data";
  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const backendStatusToChip = (status) => {
  const normalized = normalizeBackendStatus(status);

  if (!normalized.includes("PARTIAL") && isSuccessfulBackendStatus(status)) {
    return { status: toTitleStatus(status), type: "matched" };
  }

  if (
    normalized.includes("PO MISSING") ||
    normalized.includes("INVOICE MISSING") ||
    normalized.includes("GRN MISSING") ||
    normalized.includes("MRN MISSING") ||
    normalized.includes("MISSING") ||
    normalized.includes("UNABLE TO MATCH") ||
    normalized.includes("UNVERIFIED")
  ) {
    return { status: toTitleStatus(status), type: "missingDependency" };
  }

  if (
    normalized.includes("NOT COMPARED") ||
    normalized.includes("NOT APPLICABLE")
  ) {
    return { status: toTitleStatus(status), type: "notCompared" };
  }

  if (normalized.includes("INCOMPLETE") || normalized.includes("IN COMPLETE")) {
    return { status: toTitleStatus(status), type: "incomplete" };
  }

  if (
    normalized.includes("MISMATCH") ||
    normalized.includes("EXCEED") ||
    normalized.includes("REJECT") ||
    normalized.includes("FAILED")
  ) {
    return { status: toTitleStatus(status), type: "rejected" };
  }

  if (normalized.includes("PARTIAL")) {
    return { status: toTitleStatus(status), type: "partial" };
  }

  return { status: toTitleStatus(status), type: "partial" };
};

const buildStopwords = (poItems, grnItems, invoiceItems) => {
  const freq = {};
  const allTexts = [
    ...poItems.map(
      (x) => x.description || x.description_of_goods || x.item_name || ""
    ),
    ...grnItems.map((x) => x.item_name || x.description || ""),
    ...invoiceItems.map(
      (x) => x.item_name || x.item_description || x.description || ""
    ),
  ];

  // For very small datasets, stopwording becomes too aggressive and breaks matching.
  // Disable stopwords to avoid generating duplicate/extra line items in the UI.
  if (allTexts.length < 10) return new Set();

  allTexts.forEach((txt) => {
    clean(txt)
      .split(" ")
      .filter(Boolean)
      .forEach((w) => (freq[w] = (freq[w] || 0) + 1));
  });
  const total = Math.max(allTexts.length, 1);
  return new Set(Object.keys(freq).filter((w) => freq[w] / total > 0.6));
};

const keywordsFrom = (s, stopwords) =>
  clean(s)
    .split(" ")
    .filter((w) => w.length > 1 && !stopwords.has(w));

const sameByText = (left, right, stopwords) => {
  const A = new Set(keywordsFrom(left, stopwords));
  const B = new Set(keywordsFrom(right, stopwords));
  let common = 0;
  A.forEach((t) => B.has(t) && common++);
  return common >= 2 || (common >= 1 && (A.size <= 2 || B.size <= 2));
};

// Status Chip Component for modal
const ModalStatusChip = ({ status, type, fontWeight = 600 }) => {
  const config = {
    missingDependency: {
      color: "#7C2D12",
      bgcolor: "#FFF7ED",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
      border: "#FDBA74",
    },
    incomplete: {
      color: "#B45309",
      bgcolor: "#FEF3C7",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
      border: "#F59E0B",
    },
    notCompared: {
      color: "#475569",
      bgcolor: "#F1F5F9",
      icon: <InfoIcon style={{ fontSize: 14 }} />,
      border: "#CBD5E1",
    },
    matched: {
      color: "#2E7D32",
      bgcolor: "#E8F5E9",
      icon: <CheckCircleIcon style={{ fontSize: 14 }} />,
    },
    partial: {
      color: "#ED6C02",
      bgcolor: "#FFF3E0",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
    },
    rejected: {
      color: "#D32F2F",
      bgcolor: "#FFEBEE",
      icon: <CancelIcon style={{ fontSize: 14 }} />,
    },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, icon, border } = config[type] || config.default;
  const chipBorder = border || `${color}30`;

  return (
    <Chip
      icon={icon}
      label={status}
      size="small"
      sx={{
        fontFamily: GILROY_MEDIUM,
        fontSize: "14px",
        fontWeight,
        height: "30px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${chipBorder}`,
        "& .MuiChip-label": {
          px: 0.5,
        },
        "& .MuiChip-icon": {
          color: color,
          fontSize: "14px",
        },
      }}
    />
  );
};

// ---------- Component ----------
const ThreeWayMatchTable = ({
  poData,
  invoiceData,
  grnData,
  paymentAdviceData,
  matchingDetails,
  open,
  onClose,
  showSummary,
  selectedCard,
  fullPageView = false,
}) => {
  const [modalData, setModalData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const isExternallyOpen = Boolean(open);

  const closeDialog = () => {
    if (typeof onClose === "function") onClose();
    setShowModal(false);
  };
  const compareValues = (leftValue, rightValue, leftLabel, rightLabel) => {
    const left = Number(leftValue) || 0;
    const right = Number(rightValue) || 0;

    return {
      left,
      right,
      leftLabel,
      rightLabel,
      variance: left - right,
      varianceType:
        left > right
          ? `${leftLabel} > ${rightLabel}`
          : left < right
          ? `${leftLabel} < ${rightLabel}`
          : "Equal",
      status:
        left > right
          ? { status: "Exceeding", type: "rejected" }
          : left < right
          ? { status: "Partial", type: "partial" }
          : { status: "Matched", type: "matched" },
    };
  };

  const getQuantityComparison = ({ poQty, grnQty, invQty, hasPo, hasGrn, hasInv }) => {
    const normalizedPo = Number(poQty) || 0;
    const normalizedGrn = Number(grnQty) || 0;
    const normalizedInv = Number(invQty) || 0;

    if (!hasPo && !hasGrn && !hasInv) {
      return {
        status: { status: "No Data", type: "partial" },
        variance: 0,
        varianceType: "No Data",
        leftLabel: "PO",
        rightLabel: "Invoice",
        leftValue: 0,
        rightValue: 0,
        allThreeMatched: false,
      };
    }

    if (
      hasPo &&
      hasGrn &&
      hasInv &&
      normalizedPo === normalizedGrn &&
      normalizedPo === normalizedInv
    ) {
      return {
        status: { status: "Matched", type: "matched" },
        variance: 0,
        varianceType: "PO = MRN = Invoice",
        leftLabel: "Invoice",
        rightLabel: "MRN",
        leftValue: normalizedInv,
        rightValue: normalizedGrn,
        allThreeMatched: true,
      };
    }

    if (hasGrn && hasInv) {
      const comparison = compareValues(normalizedInv, normalizedGrn, "Invoice", "MRN");
      return {
        ...comparison,
        leftValue: normalizedInv,
        rightValue: normalizedGrn,
        allThreeMatched: false,
      };
    }

    if (hasPo && hasInv) {
      const comparison = compareValues(normalizedInv, normalizedPo, "Invoice", "PO");
      return {
        ...comparison,
        leftValue: normalizedInv,
        rightValue: normalizedPo,
        allThreeMatched: false,
      };
    }

    if (hasPo && hasGrn) {
      const comparison = compareValues(normalizedGrn, normalizedPo, "MRN", "PO");
      return {
        ...comparison,
        leftValue: normalizedGrn,
        rightValue: normalizedPo,
        allThreeMatched: false,
      };
    }

    return {
      status: { status: "No Data", type: "partial" },
      variance: 0,
      varianceType: "No Data",
      leftLabel: "PO",
      rightLabel: "Invoice",
      leftValue: normalizedPo,
      rightValue: normalizedInv,
      allThreeMatched: false,
    };
  };

  // Helper functions for status analysis
  // 1. Item-Level Quantity Status
  const getQuantityStatusForItem = (poQty, grnQty, invQty, hasPo, hasGrn, hasInv) =>
    getQuantityComparison({
      poQty,
      grnQty,
      invQty,
      hasPo,
      hasGrn,
      hasInv,
    }).status;
  // 2. Item-Level Price Status
  const getPriceStatusForItem = (poPrice, invPrice) => {
    poPrice = Number(poPrice) || 0;
    invPrice = Number(invPrice) || 0;

    if (poPrice === 0 && invPrice === 0) {
      return { status: "No Data", type: "partial" };
    }

    // ðŸ”´ REJECTED: Price INCREASED (greater than)
    if (invPrice > poPrice) {
      return { status: "Increased", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: Price DECREASED (smaller than)
    if (invPrice < poPrice) {
      return { status: "Partial", type: "partial" };
    }

    // âœ… MATCHED: Same price
    if (poPrice === invPrice) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "partial" };
  };

  // 3. Item-Level Item Status
  const getItemStatusForRow = (row, poItems, grnItems, invItems) => {
    const targetCodeA = clean(row.partNo || "");
    const targetCodeB = clean(row.itemCode || "");
    const targetText = clean(row.item || "");

    const getMatchQuality = (item, codeKeys, textKeys) => {
      const itemCode = clean(pick(item, codeKeys) || "");
      const itemText = clean(pick(item, textKeys) || "");

      const codeExact =
        itemCode &&
        ((targetCodeA && itemCode === targetCodeA) ||
          (targetCodeB && itemCode === targetCodeB));

      if (codeExact) return "exact";

      if (itemText && targetText && itemText === targetText) return "exact";

      const textPartial =
        itemText &&
        targetText &&
        (itemText.includes(targetText) ||
          targetText.includes(itemText) ||
          sameByText(itemText, targetText, new Set()));

      if (textPartial) return "partial";

      return "none";
    };

    const getBestMatch = (items, codeKeys, textKeys) =>
      items.reduce((best, item) => {
        if (best === "exact") return best;
        const quality = getMatchQuality(item, codeKeys, textKeys);
        return quality === "exact" || quality === "partial" ? quality : best;
      }, "none");

    const poMatch = getBestMatch(
      poItems,
      ["part_no", "part_number", "partno", "item_code", "itemcode"],
      ["description_of_goods", "description", "item_name"]
    );

    const grnMatch = getBestMatch(
      grnItems,
      ["part_no", "part_number", "partno", "item_code", "code"],
      ["item_name", "description", "description_of_goods"]
    );

    const invoiceMatch = getBestMatch(
      invItems,
      ["part_no", "part_number", "partno", "item_code", "code", "cat_code"],
      [
        "item_name",
        "description",
        "item_description",
        "description_of_goods",
        "particulars",
      ]
    );

    if (poMatch === "none") {
      return { status: "Extra Item", type: "rejected" };
    }

    if (grnMatch === "none" || invoiceMatch === "none") {
      return { status: "Missing", type: "partial" };
    }

    if (poMatch === "partial" || grnMatch === "partial" || invoiceMatch === "partial") {
      return { status: "Partial Match", type: "partial" };
    }

    return { status: "Matched", type: "matched" };
  };

  const findSourceLineItem = (items, comparisonItem = {}, description, partNumber) => {
    const targetPart = clean(partNumber || comparisonItem?.part_number || "");
    const targetName = clean(description || comparisonItem?.item_name || "");

    return items.find((item) => {
      const itemPart = clean(
        pick(item, ["part_number", "part_no", "partno", "item_code", "itemcode"]) ||
          ""
      );
      return targetPart && itemPart && itemPart === targetPart;
    }) ||
      items.find((item) => {
        const itemName = clean(
          pick(item, [
            "description",
            "description_of_goods",
            "item_name",
            "item_description",
            "particulars",
          ]) || ""
        );
        return targetName && itemName && itemName === targetName;
      });
  };

  const buildRowsFromMatchingDetails = (details, poItems, grnItems, invoiceItems) => {
    const comparisons = asArray(details?.item_comparisons);

    return comparisons.map((comparison) => {
      const description =
        comparison?.description ||
        comparison?.po?.item_name ||
        comparison?.invoice?.item_name ||
        comparison?.grn?.item_name ||
        "-";
      const partNumber =
        comparison?.part_number ||
        comparison?.po?.part_number ||
        comparison?.invoice?.part_number ||
        comparison?.grn?.part_number ||
        "";

      const poLine = findSourceLineItem(
        poItems,
        comparison?.po,
        description,
        partNumber
      );
      const invoiceLine = findSourceLineItem(
        invoiceItems,
        comparison?.invoice,
        description,
        partNumber
      );
      const grnLine = findSourceLineItem(
        grnItems,
        comparison?.grn,
        description,
        partNumber
      );

      const poQty =
        parseMoney(comparison?.po_quantity ?? comparison?.po?.quantity) || 0;
      const grnQty =
        parseMoney(comparison?.grn_quantity ?? comparison?.grn?.quantity) || 0;
      const invoiceQty =
        parseMoney(
          comparison?.invoice_quantity ?? comparison?.invoice?.quantity
        ) || 0;
      const unitPricePO =
        parseMoney(comparison?.po_rate ?? comparison?.po?.rate) || 0;
      const unitPriceInvoice =
        parseMoney(comparison?.invoice_rate ?? comparison?.invoice?.rate) || 0;
      const totalPricePO =
        parseMoney(
          comparison?.amount_comparison?.po_line_amount ??
            comparison?.po?.line_amount ??
            poLine?.line_amount ??
            poLine?.taxable_amount
        ) ?? poQty * unitPricePO;
      const totalPriceInvoice =
        parseMoney(
          comparison?.amount_comparison?.invoice_line_amount ??
            comparison?.invoice?.line_amount ??
            invoiceLine?.line_amount ??
            invoiceLine?.taxable_amount
        ) ?? invoiceQty * unitPriceInvoice;
      const amountDifference =
        parseMoney(comparison?.amount_comparison?.amount_difference);
      const quantityComparison = getQuantityComparison({
        poQty,
        grnQty,
        invQty: invoiceQty,
        hasPo: Boolean(comparison?.po || poLine),
        hasGrn: Boolean(comparison?.grn || grnLine),
        hasInv: Boolean(comparison?.invoice || invoiceLine),
      });
      const quantityStatus = backendStatusToChip(
        comparison?.comparison_status?.quantity_matched || comparison?.status
      );
      const priceStatus = backendStatusToChip(
        comparison?.comparison_status?.line_amount_matched ||
          comparison?.amount_comparison?.status ||
          comparison?.status
      );
      const itemStatus = backendStatusToChip(
        comparison?.comparison_status?.item_matched || comparison?.status
      );

      return {
        item: description,
        partNo: toDash(partNumber),
        itemCode: toDash(
          pick(poLine || invoiceLine || grnLine || {}, [
            "item_code",
            "itemcode",
            "code",
          ])
        ),
        poQty,
        grnQty,
        invoiceQty,
        unitPricePO,
        unitPriceInvoice,
        totalPricePO,
        totalPriceInvoice,
        taxRate: pick(invoiceLine || poLine || {}, [
          "gst_percent",
          "tax_rate",
          "igst_rate",
          "gst_rate",
          "cgst_rate",
          "sgst_rate",
        ]),
        quantityVariance: quantityComparison.variance,
        priceVariance:
          amountDifference !== null ? amountDifference : totalPricePO - totalPriceInvoice,
        quantityVarianceType: quantityComparison.varianceType,
        priceVarianceType:
          comparison?.amount_comparison?.status || "PO vs Invoice",
        quantityComparisonLeftLabel: quantityComparison.leftLabel,
        quantityComparisonRightLabel: quantityComparison.rightLabel,
        quantityComparisonLeftValue: quantityComparison.leftValue,
        quantityComparisonRightValue: quantityComparison.rightValue,
        allThreeQuantityMatched: quantityComparison.allThreeMatched,
        quantityStatus,
        priceStatus,
        itemStatus,
        backendReasons: asArray(comparison?.reasons),
      };
    });
  };

  const buildMergedRows = (poItems, grnItems, invoiceItems, STOPWORDS) => {
    const usedGrnIndexes = new Set();
    const usedInvIndexes = new Set();

    const findPoByCodeOrText = (partNo, itemCode, desc) =>
      poItems.find(
        (p) =>
          clean(pick(p, ["part_no", "part_number", "partno", "item_code", "itemcode"]) || "") ===
            clean(partNo || itemCode || "") ||
          clean(pick(p, ["part_no", "part_number", "partno", "item_code", "itemcode"]) || "") ===
            clean(itemCode || partNo || "")
      ) ||
      poItems.find((p) =>
        sameByText(
          desc || "",
          pick(p, ["description_of_goods", "description", "item_name"]) || "",
          STOPWORDS
        )
      );

    const findGrnByCodeOrText = (partNo, itemCode, desc) => {
      const codeA = clean(partNo || itemCode || "");
      const codeB = clean(itemCode || partNo || "");

      const exactIdx = grnItems.findIndex((g, idx) => {
        if (usedGrnIndexes.has(idx)) return false;
        const code = clean(pick(g, ["item_code", "part_no", "partno","part_number"]) || "");
        return code && (code === codeA || code === codeB);
      });

      if (exactIdx >= 0) {
        usedGrnIndexes.add(exactIdx);
        return grnItems[exactIdx];
      }

      const candidates = [];
      grnItems.forEach((g, idx) => {
        if (usedGrnIndexes.has(idx)) return;
        const gName =
          pick(g, ["item_name", "description", "description_of_goods"]) || "";
        if (!gName) return;
        if (sameByText(desc || "", gName, STOPWORDS)) {
          candidates.push({ idx, g });
        }
      });

      if (candidates.length === 0) return undefined;
      if (candidates.length === 1) {
        usedGrnIndexes.add(candidates[0].idx);
        return candidates[0].g;
      }

      const poAmount = parseMoney(
        pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
          "parsed_amount",
          "amount",
          "total",
          "line_total",
        ])
      );

      candidates.sort((a, b) => {
        if (poAmount == null) return a.idx - b.idx;
        const aAmt =
          parseMoney(pick(a.g || {}, ["total_price", "total_amount", "amount"])) ??
          0;
        const bAmt =
          parseMoney(pick(b.g || {}, ["total_price", "total_amount", "amount"])) ??
          0;
        return Math.abs(aAmt - poAmount) - Math.abs(bAmt - poAmount);
      });

      usedGrnIndexes.add(candidates[0].idx);
      return candidates[0].g;
    };

    const findInvByCodeOrText = (partNo, itemCode, desc, grnRef) => {
      const codeA = clean(partNo || "");
      const codeB = clean(itemCode || "");

      const exactIdx = invoiceItems.findIndex((inv, idx) => {
        if (usedInvIndexes.has(idx)) return false;
        const code = clean(pick(inv, ["item_code", "part_no", "partno","part_number"]) || "");
        return code && (code === codeA || code === codeB);
      });

      if (exactIdx >= 0) {
        usedInvIndexes.add(exactIdx);
        return invoiceItems[exactIdx];
      }

      const needle = desc || pick(grnRef || {}, ["item_name"]) || "";
      const textCandidates = [];
      invoiceItems.forEach((inv, idx) => {
        if (usedInvIndexes.has(idx)) return;
        const hay =
          pick(inv, ["item_name", "description", "item_description", "particulars"]) ||
          "";
        if (!hay) return;
        if (sameByText(needle, hay, STOPWORDS)) {
          textCandidates.push({ idx, inv });
        }
      });

      let invoice = undefined;
      if (textCandidates.length === 1) {
        invoice = textCandidates[0].inv;
        usedInvIndexes.add(textCandidates[0].idx);
      } else if (textCandidates.length > 1) {
        const poAmount = parseMoney(
          pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
            "parsed_amount",
            "amount",
            "total",
            "line_total",
          ])
        );

        textCandidates.sort((a, b) => {
          if (poAmount == null) return a.idx - b.idx;
          const aAmt =
            parseMoney(
              pick(a.inv || {}, ["parsed_amount", "amount", "line_total", "total"])
            ) ?? 0;
          const bAmt =
            parseMoney(
              pick(b.inv || {}, ["parsed_amount", "amount", "line_total", "total"])
            ) ?? 0;
          return Math.abs(aAmt - poAmount) - Math.abs(bAmt - poAmount);
        });

        invoice = textCandidates[0].inv;
        usedInvIndexes.add(textCandidates[0].idx);
      }

      if (!invoice) {
        const poHSN = pick(
          findPoByCodeOrText(partNo, itemCode, desc) || {},
          ["hsn_code", "hsn"]
        );

        const fallbackIdx = invoiceItems.findIndex((inv, idx) => {
          if (usedInvIndexes.has(idx)) return false;
          const invHSN = pick(inv, ["hsn", "hsn_sac"]);
          const poRate = parseMoney(
            pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
              "parsed_rate",
              "rate",
              "unit_price",
              "price",
            ])
          );
          const invRate = parseMoney(
            pick(inv, [
              "unit_rate",
              "rate",
              "unit_price",
              "price",
              "parsed_rate",
            ])
          );
          const rateClose =
            poRate != null &&
            invRate != null &&
            Math.abs(poRate - invRate) < 0.01;
          return (
            (poHSN && invHSN && String(poHSN) === String(invHSN)) || rateClose
          );
        });

        if (fallbackIdx >= 0) {
          invoice = invoiceItems[fallbackIdx];
          usedInvIndexes.add(fallbackIdx);
        }
      }

      return invoice;
    };

    const buildRow = ({ partNo, itemCode, desc, poItem, grnItem, invItem }) => {
      const poQty =
        parseMoney(
          pick(poItem || {}, ["po_qty", "qty.", "qty", "quantity", "po_quantity"])
        ) || 0;

      const grnQty =
        parseMoney(
          pick(grnItem || {}, [
            "received_q",
            "received_qty",
            "quantity_received",
            "mrn_qty",
            "qty",
            "unt",
            "received_quantity",
            "received_qty.",
          ])
        ) || 0;

      const invQty =
        parseMoney(
          pick(invItem || {}, [
            "order_quantity",
            "quantity",
            "qty",
            "invoice_qty",
            "parsed_qty",
            "parsed_quantity",
            "unt",
          ])
        ) || 0;

      const unitPricePO =
        parseMoney(
          pick(poItem || {}, ["parsed_rate", "rate", "unit_price", "price"])
        ) || 0;
      const unitPriceInvoice =
        parseMoney(
          pick(invItem || {}, [
            "unit_rate",
            "unit_price",
            "rate",
            "price",
            "parsed_rate",
          ])
        ) || 0;

      let totalPricePO = parseMoney(
        pick(poItem || {}, ["parsed_amount", "amount", "total", "line_total"])
      );
      if (totalPricePO === undefined || totalPricePO === null) {
        totalPricePO = poQty * unitPricePO;
      }

      let totalPriceInvoice = parseMoney(
        pick(invItem || {}, ["parsed_amount", "line_total", "amount", "total"])
      );
      if (totalPriceInvoice === undefined || totalPriceInvoice === null) {
        totalPriceInvoice = invQty * unitPriceInvoice;
      }

      const hasPoItem = Boolean(poItem);
      const hasGrnItem = Boolean(grnItem);
      const hasInvItem = Boolean(invItem);
      const quantityComparison = getQuantityComparison({
        poQty,
        grnQty,
        invQty,
        hasPo: hasPoItem,
        hasGrn: hasGrnItem,
        hasInv: hasInvItem,
      });
      const quantityVariance = quantityComparison.variance;
      const priceVariance = totalPricePO - totalPriceInvoice;
      const priceVarianceType =
        totalPricePO > totalPriceInvoice
          ? "PO Total > Invoice Total"
          : totalPricePO < totalPriceInvoice
          ? "PO Total < Invoice Total"
          : "Equal";

      const quantityStatus = getQuantityStatusForItem(
        poQty,
        grnQty,
        invQty,
        hasPoItem,
        hasGrnItem,
        hasInvItem
      );
      const priceStatus = getPriceStatusForItem(
        unitPricePO,
        unitPriceInvoice
      );
      const itemStatus = getItemStatusForRow(
        {
          partNo: partNo || "",
          itemCode: itemCode || "",
          item: desc || "",
        },
        poItems,
        grnItems,
        invoiceItems
      );

      return {
        item: desc || "-",
        partNo: toDash(partNo),
        itemCode: toDash(itemCode),
        poQty: poQty,
        grnQty: grnQty,
        invoiceQty: invQty,
        unitPricePO: unitPricePO,
        unitPriceInvoice: unitPriceInvoice,
        totalPricePO: totalPricePO,
        totalPriceInvoice: totalPriceInvoice,
        quantityVariance: quantityVariance,
        priceVariance: priceVariance,
        quantityVarianceType: quantityComparison.varianceType,
        priceVarianceType: priceVarianceType,
        quantityComparisonLeftLabel: quantityComparison.leftLabel,
        quantityComparisonRightLabel: quantityComparison.rightLabel,
        quantityComparisonLeftValue: quantityComparison.leftValue,
        quantityComparisonRightValue: quantityComparison.rightValue,
        allThreeQuantityMatched: quantityComparison.allThreeMatched,
        quantityStatus: quantityStatus,
        priceStatus: priceStatus,
        itemStatus: itemStatus,
      };
    };

    const merged = poItems.map((poItem) => {
      const partNo = pick(poItem, [
        "part_no",
        "partno",
        "item_code",
        "itemcode",
        "part_number"
      ]);
      const itemCode = pick(poItem, [
        "item_code",
        "itemcode",
        "part_no",
        "partno",
        "part_number"
      ]);
      const poDesc =
        pick(poItem, ["description_of_goods", "description", "item_name"]) ||
        "";

      const grn = findGrnByCodeOrText(partNo, itemCode, poDesc);
      const invoice = findInvByCodeOrText(partNo, itemCode, poDesc, grn);

      return buildRow({
        partNo,
        itemCode,
        desc: poDesc,
        poItem,
        grnItem: grn,
        invItem: invoice,
      });
    });

    const seen = new Set(
      merged.map(
        (r) =>
          `${clean(r.partNo || r.itemCode || "")}|${clean(r.item || "")}|${r.invoiceQty}|${r.grnQty}`
      )
    );

    grnItems.forEach((grnItem) => {
      const partNo = pick(grnItem, ["part_no", "part_number", "partno", "item_code", "code"]);
      const itemCode = pick(grnItem, [
        "item_code",
        "code",
        "part_no",
        "part_number",
        "partno",
      ]);
      const desc =
        pick(grnItem, ["item_name", "description", "description_of_goods"]) ||
        "";
      const poItem = findPoByCodeOrText(partNo, itemCode, desc);
      if (poItem) return;

      const invItem = findInvByCodeOrText(partNo, itemCode, desc, grnItem);
      const row = buildRow({
        partNo,
        itemCode,
        desc,
        poItem: null,
        grnItem,
        invItem,
      });

      const rowKey = `${clean(row.partNo || row.itemCode || "")}|${clean(
        row.item || ""
      )}|${row.invoiceQty}|${row.grnQty}`;
      if (!seen.has(rowKey)) {
        seen.add(rowKey);
        merged.push(row);
      }
    });

    invoiceItems.forEach((invItem) => {
      const partNo = pick(invItem, ["part_no", "part_number", "partno", "item_code", "code"]);
      const itemCode = pick(invItem, [
        "item_code",
        "code",
        "part_no",
        "part_number",
        "partno",
      ]);
      const desc =
        pick(invItem, [
          "item_name",
          "description",
          "item_description",
          "particulars",
        ]) || "";
      const poItem = findPoByCodeOrText(partNo, itemCode, desc);
      if (poItem) return;
      const grnItem = findGrnByCodeOrText(partNo, itemCode, desc);
      const row = buildRow({
        partNo,
        itemCode,
        desc,
        poItem: null,
        grnItem,
        invItem,
      });

      const rowKey = `${clean(row.partNo || row.itemCode || "")}|${clean(
        row.item || ""
      )}|${row.invoiceQty}|${row.grnQty}`;
      if (!seen.has(rowKey)) {
        seen.add(rowKey);
        merged.push(row);
      }
    });

    return merged;
  };

  useEffect(() => {
    if (isExternallyOpen || showModal) {
      const poItems = poData?.[0]?.line_items || [];
      const grnItems = grnData?.[0]?.line_items || [];
      const invoiceItems = invoiceData?.[0]?.line_items || [];

      if (asArray(matchingDetails?.item_comparisons).length > 0) {
        setModalData(
          buildRowsFromMatchingDetails(
            matchingDetails,
            poItems,
            grnItems,
            invoiceItems
          )
        );
        return;
      }

      const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

      const merged = buildMergedRows(poItems, grnItems, invoiceItems, STOPWORDS);

      setModalData(merged);
    }
  }, [open, poData, grnData, invoiceData, matchingDetails]);

  const formatNumber = (v) => {
    const n = parseMoney(v);
    if (n === 0) return "0.00";
    if (n == null) return "-";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getDocumentTotalValue = (document = {}, keys = []) => {
    const value = pick(document, keys);
    return value === undefined ? null : value;
  };

  const getPoTotal = (keys = []) => getDocumentTotalValue(poData?.[0], keys);
  const getInvoiceTotal = (keys = []) =>
    getDocumentTotalValue(invoiceData?.[0], keys);
  const getGrnTotal = (keys = []) => getDocumentTotalValue(grnData?.[0], keys);
  const payment_advice_data = paymentAdviceData;
  const paymentAdvice = payment_advice_data?.[0];
  const po = poData?.[0];
  const grn = grnData?.[0];
  const invoice = invoiceData?.[0];
  const paymentReconciliation = {
    ...(matchingDetails?.payment_advice_summary || {}),
    ...(matchingDetails?.payment_reconciliation || {}),
    ...(matchingDetails?.payment_advice_reconciliation || {}),
    ...(matchingDetails?.result_json?.payment_advice_matching || {}),
    ...(matchingDetails?.payment_advice_matching || {}),
    ...(matchingDetails || {}),
  };
  const reconciliationInvoice = paymentReconciliation?.invoice || invoice || {};
  const mismatchReasonSource =
    paymentReconciliation?.mismatch_reasons ??
    matchingDetails?.payment_advice_matching?.mismatch_reasons;
  const mismatchReasons = Array.isArray(mismatchReasonSource)
    ? mismatchReasonSource.filter(Boolean)
    : [];
  const hasPaymentAdvice =
    (Array.isArray(payment_advice_data) && payment_advice_data.length > 0) ||
    Boolean(
      paymentReconciliation?.payment_status ||
        reconciliationInvoice?.advance_paid !== undefined ||
        reconciliationInvoice?.expected_balance !== undefined,
    );

  const totalKeys = {
    gross: [
      "total_tax_included",
      "tax_included_total",
      "total_with_tax",
      "invoice_amount",
      "total_amount",
      "grand_total",
      "amount",
    ],
    taxExcluded: [
      "total_tax_excluded",
      "tax_excluded_total",
      "total_without_tax",
      "taxable_amount",
      "subtotal",
    ],
    taxIncluded: [
      "total_tax_included",
      "tax_included_total",
      "total_with_tax",
      "grand_total",
      "invoice_amount",
      "total_amount",
    ],
  };

  const formatVariance = (variance, type = "number") => {
    if (variance === 0) return "0";
    if (!variance) return "-";

    const absVariance = Math.abs(variance);
    const formattedValue =
      type === "number"
        ? absVariance.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })
        : Math.round(absVariance).toString();

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {variance > 0 ? (
          <ArrowUpwardIcon sx={{ color: "#D84315", fontSize: 16, mr: 0.5 }} />
        ) : variance < 0 ? (
          <ArrowDownwardIcon sx={{ color: "#2E7D32", fontSize: 16, mr: 0.5 }} />
        ) : null}
        {formattedValue}
      </Box>
    );
  };

  const getVarianceTooltip = (row, type) => {
    if (type === "quantity") {
      if (row.allThreeQuantityMatched) {
        return `PO (${row.poQty}), MRN (${row.grnQty}) and Invoice (${row.invoiceQty}) quantities match`;
      }

      if (row.quantityVariance > 0) {
        return `${row.quantityComparisonLeftLabel} quantity (${row.quantityComparisonLeftValue}) is greater than ${row.quantityComparisonRightLabel} quantity (${row.quantityComparisonRightValue})`;
      } else if (row.quantityVariance < 0) {
        return `${row.quantityComparisonLeftLabel} quantity (${row.quantityComparisonLeftValue}) is less than ${row.quantityComparisonRightLabel} quantity (${row.quantityComparisonRightValue})`;
      } else {
        return `${row.quantityComparisonLeftLabel} and ${row.quantityComparisonRightLabel} quantities match`;
      }
    } else {
      if (row.priceVariance > 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is greater than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else if (row.priceVariance < 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is less than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else {
        return "PO and Invoice total prices match";
      }
    }
  };

  const rawAddress = (() => {
    const addr = poData?.[0]?.shipping_address;

    if (!addr) return "No shipping address provided";

    if (typeof addr === "string") return addr;

    if (typeof addr === "object" && addr.address) return addr.address;

    return "No shipping address provided";
  })();

  const addressLines = rawAddress.split("\n");
  const stickySerialWidth = 72;
  const stickyDescriptionWidth = 260;
  const stickyOverallMatchWidth = 190;
  const stickyColumnHeaderBg = "#eaf2ff";
  const stickyColumnBodyBg = "#f8fbff";

  const fullPageSummaryItems = [
    {
      label: "TWM ID",
      value: firstDisplayValue(
        selectedCard?.match_code,
        selectedCard?.twm_code,
        matchingDetails?.match_code,
        matchingDetails?.twm_code
      ),
      tone: "primary",
    },
    {
      label: "Matching Type",
      value: formatMatchingType(
        firstDisplayValue(
          selectedCard?.matching_mode,
          matchingDetails?.matching_mode,
          matchingDetails?.matching_type
        )
      ),
      tone: "info",
    },
    {
      label: "Supplier",
      value: firstDisplayValue(
        po?.vendor,
        po?.vendor_name,
        po?.supplier_name,
        invoice?.vendor_name,
        invoice?.supplier_name,
        grn?.vendor_name,
        grn?.vendor
      ),
    },
    {
      label: "PO Number",
      value: firstDisplayValue(
        po?.purchase_order_no,
        po?.purchase_order_number,
        po?.po_number,
        po?.po_no,
        invoice?.purchase_order_number,
        grn?.purchase_order_no
      ),
    },
    {
      label: "Invoice Number",
      value: firstDisplayValue(
        invoice?.invoice_number,
        invoice?.invoice_no,
        invoice?.bill_number,
        invoice?.bill_no
      ),
    },
    {
      label: "GRN Number",
      value: firstDisplayValue(
        grn?.grn_number,
        grn?.grn_no,
        grn?.goods_receipt_no,
        grn?.receipt_number
      ),
    },
    {
      label: "Status",
      value: firstDisplayValue(
        matchingDetails?.overall_status,
        matchingDetails?.status,
        selectedCard?.status,
        selectedCard?.approval_status
      ),
      tone: "status",
    },
    {
      label: "Line Items",
      value: modalData.length || getLineItemCount(poData) || getLineItemCount(invoiceData) || getLineItemCount(grnData),
    },
  ];

  const summaryToneSx = {
    primary: { borderColor: "#cfd5df", backgroundColor: "#ffffff" },
    info: { borderColor: "#cfd5df", backgroundColor: "#ffffff" },
    status: { borderColor: "#98e8d0", backgroundColor: "#eef7ff" },
  };

  const renderFullPageSummary = () => {
    if (!fullPageView) return null;

    return (
      <Paper
        className="matching-view-summary"
        elevation={0}
        sx={{
          border: 0,
          borderRadius: 0,
          backgroundColor: "transparent",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: "4px",
          }}
        >
          {fullPageSummaryItems.map((item) => (
            <Box
              key={item.label}
              data-field={item.label}
              sx={{
                minHeight: 66,
                border: "1px solid #cfd5df",
                borderRadius: 0,
                backgroundColor: "#ffffff",
                px: 1.6,
                py: 1.3,
                ...summaryToneSx[item.tone],
              }}
            >
              <Typography
                sx={{
                  color: "#64748b",
                  fontFamily: GILROY_MEDIUM,
                  fontSize: fullPageView ? "14px" : "10px",
                  fontWeight: 700,
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  mb: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                title={String(item.value || "-")}
                sx={{
                  color: "#0f172a",
                  fontFamily: GILROY_BOLD,
                  fontSize: fullPageView ? "15px" : "12px",
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {toDash(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const getOverallMatchStatus = (row = {}) => {
    const statuses = [row.itemStatus, row.quantityStatus, row.priceStatus];
    if (statuses.every((status) => status?.type === "matched")) {
      return { status: "Matched", type: "matched" };
    }
    if (statuses.some((status) => status?.type === "rejected")) {
      return { status: "Exception Found", type: "rejected" };
    }
    if (statuses.some((status) => status?.type === "missingDependency")) {
      return { status: "Missing Data", type: "missingDependency" };
    }
    if (statuses.some((status) => status?.type === "partial")) {
      return { status: "Partially Matched", type: "partial" };
    }
    return { status: "Pending", type: "notCompared" };
  };

  const toggleDescription = (rowIndex) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleViewItems = () => {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (asArray(matchingDetails?.item_comparisons).length > 0) {
      setModalData(
        buildRowsFromMatchingDetails(
          matchingDetails,
          poItems,
          grnItems,
          invoiceItems
        )
      );
      setShowModal(true);
      return;
    }

    const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

    const merged = buildMergedRows(poItems, grnItems, invoiceItems, STOPWORDS);

    setModalData(merged);
    setShowModal(true);
  };

  const summaryHeaderCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    backgroundColor: "#E8F0F4",
    borderBottom: "1px solid #e2e8f0",
    py: 1.1,
    px: 1.5,
  };

  const summaryKeyCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 1.2,
    px: 1.5,
    width: 210,
    minWidth: 210,
  };

  const summaryValueCellSx = {
    fontSize: "14px",
    fontFamily: GILROY_MEDIUM,
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 1.2,
    px: 1.5,
    verticalAlign: "middle",
  };

  const renderRow = (
    label,
    poValue,
    invoiceValue,
    grnValue,
    paymentAdviceValue,
  ) => (
    <TableRow>
      <TableCell sx={summaryKeyCellSx}>{label}</TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 260, minWidth: 230 }}>
        {toDash(poValue)}
      </TableCell>
      {hasPaymentAdvice && (
        <TableCell sx={{ ...summaryValueCellSx, width: 240, minWidth: 210 }}>
          {toDash(paymentAdviceValue)}
        </TableCell>
      )}
      <TableCell sx={{ ...summaryValueCellSx, width: 210, minWidth: 190 }}>
        {toDash(invoiceValue)}
      </TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 210, minWidth: 190 }}>
        {toDash(grnValue)}
      </TableCell>
    </TableRow>
  );

  const renderTotalTile = (label, value) => (
    <Box
      className="matching-view-tile"
      data-field={label}
      sx={{
        px: fullPageView ? 1.4 : 1,
        py: fullPageView ? 1.1 : 0.75,
        borderRadius: fullPageView ? 0 : 1.25,
        backgroundColor: "#ffffff",
        border: fullPageView ? "1px solid #cfd5df" : "1px solid #e2e8f0",
        minHeight: fullPageView ? 58 : 54,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontFamily: GILROY_MEDIUM,
          letterSpacing: 0,
          textTransform: "uppercase",
          color: "#64748b",
          mb: 0.15,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CurrencyRupeeIcon sx={{ fontSize: 16 }} />
        <Typography
          sx={{
            fontSize: fullPageView ? "16px" : "14px",
            fontFamily: GILROY_BOLD,
            fontWeight: 700,
            lineHeight: 1.2,
            color: "#0f172a",
          }}
        >
          {formatNumber(value)}
        </Typography>
      </Box>
    </Box>
  );

  const renderPaymentDetail = (label, value) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }
    const amountLabels = ["Advice Paid", "Balance Due", "Invoice Amount"];
    const isAmountValue = amountLabels.includes(label);

    return (
      <Box
        className="matching-view-tile"
        data-field={label}
        sx={{
          px: fullPageView ? 1.4 : 1,
          py: fullPageView ? 1.1 : 0.75,
          borderRadius: fullPageView ? 0 : 1.25,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
          border: fullPageView ? "1px solid #cfd5df" : "1px solid #dbeafe",
          boxShadow: fullPageView ? "none" : "0 4px 12px rgba(37, 99, 235, 0.06)",
          minHeight: fullPageView ? 58 : 54,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontFamily: GILROY_MEDIUM,
            letterSpacing: 0,
            textTransform: "uppercase",
            color: "#2563eb",
            mb: 0.15,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {isAmountValue ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CurrencyRupeeIcon sx={{ fontSize: 16, color: "#0f172a" }} />
            <Typography
              title={String(value)}
              sx={{
                fontSize: fullPageView ? "16px" : "14px",
                fontFamily: GILROY_BOLD,
                fontWeight: 700,
                lineHeight: 1.25,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatNumber(value)}
            </Typography>
          </Box>
        ) : (
          <Typography
            title={String(value)}
            sx={{
              fontSize: fullPageView ? "16px" : "14px",
              fontFamily: GILROY_BOLD,
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#1e3a8a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    );
  };

  // Inline styles
  const headerCellStyle = {
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    backgroundColor: "#e8eaf6",
    padding: "8px",
    borderRight: "1px solid #e0e0e0",
  };

  const cellStyle = {
    fontSize: "14px",
    fontFamily: GILROY_MEDIUM,
    padding: "8px",
    borderRight: "1px solid #f0f0f0",
  };

  const renderDescriptionCell = (value, rowIndex) => {
    const text = String(value || "-").trim() || "-";
    const isExpanded = Boolean(expandedDescriptions[rowIndex]);
    const isLongText = text.length > 72;

    return (
      <Box
        sx={{
          width: stickyDescriptionWidth - 24,
          maxWidth: stickyDescriptionWidth - 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
        <Tooltip
          title={text === "-" ? "" : text}
          arrow
          placement="top-start"
          slotProps={{
            tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px", maxWidth: 420 } },
          }}
        >
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: fullPageView ? "14px" : "13px",
              lineHeight: 1.45,
              fontFamily: GILROY_MEDIUM,
              color: "#1e293b",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isExpanded ? "unset" : 2,
              overflow: "hidden",
              whiteSpace: isExpanded ? "normal" : "initial",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {text}
          </Typography>
        </Tooltip>

        {isLongText ? (
          <Button
            size="small"
            variant="text"
            onClick={() => toggleDescription(rowIndex)}
            sx={{
              minWidth: "auto",
              padding: 0,
              fontSize: fullPageView ? "14px" : "11px",
              lineHeight: 1.2,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {isExpanded ? "Read less" : "Read more"}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <div>
      {showSummary && (<>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableBody>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell sx={summaryHeaderCellSx}>
                  Key
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  PO
                </TableCell>
                {hasPaymentAdvice && (
                  <TableCell sx={summaryHeaderCellSx}>
                    Payment Advice
                  </TableCell>
                )}
                <TableCell sx={summaryHeaderCellSx}>
                  Invoice
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  GRN
                </TableCell>
                <TableCell
                  rowSpan={7}
                  align="center"
                  sx={{
                    width: 150,
                    minWidth: 150,
                    verticalAlign: "middle",
                    px: 1.5,
                    borderLeft: "1px solid #e2e8f0",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={handleViewItems}
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      fontFamily: GILROY_MEDIUM,
                      minWidth: 112,
                    }}
                  >
                    View Items
                  </Button>
                </TableCell>
              </TableRow>
              {renderRow(
                hasPaymentAdvice ? "PO Reference" : "Purchase Order No",
                poData?.[0]?.purchase_order_no ||
                  poData?.[0]?.purchase_order_number ||
                  poData?.[0]?.po_number ||
                  poData?.[0]?.po_no ||
                  poData?.[0]?.order_number,
                invoiceData?.[0]?.purchase_order_number ||
                  invoiceData?.[0]?.po_number ||
                  invoiceData?.[0]?.po_no,
                grnData?.[0]?.purchase_order_no ||
                  grnData?.[0]?.purchase_order_number ||
                  grnData?.[0]?.po_number ||
                  grnData?.[0]?.po_no,
                paymentAdvice?.po_number
              )}
              {renderRow(
                hasPaymentAdvice
                  ? "Document ID"
                  : "Invoice / Goods Receipt ID",
                "-",
                invoiceData?.[0]?.invoice_number,
                grnData?.[0]?.grn_number || grnData?.[0]?.grn_no,
                paymentAdvice?.payment_advice_number
              )}
              {renderRow(
                hasPaymentAdvice ? "Supplier / Payment Mode" : "Supplier Name",
                poData?.[0]?.vendor ||
                  poData?.[0]?.vendor_name ||
                  poData?.[0]?.supplier_name,
                invoiceData?.[0]?.vendor_name ||
                  invoiceData?.[0]?.supplier_name ||
                  "-",
                grnData?.[0]?.vendor_name || grnData?.[0]?.vendor,
                paymentAdvice?.payment_product
              )}

              {renderRow(
                hasPaymentAdvice ? "Bill / Transaction Date" : "Bill Date",
                poData?.[0]?.order_date || poData?.[0]?.po_date,
                invoiceData?.[0]?.invoice_date,
                grnData?.[0]?.bill_date || grnData?.[0]?.date || grnData?.[0]?.grn_date,
                paymentAdvice?.transaction_date
              )}
              {hasPaymentAdvice &&
                renderRow(
                  "Payment Ref No",
                  "-",
                  "-",
                  "-",
                  paymentAdvice?.utr_rrn_no ||
                    paymentAdvice?.transaction_reference ||
                    paymentAdvice?.payment_reference ||
                    paymentAdvice?.payment_advice_number
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Advance Paid",
                  "-",
                  firstDisplayValue(
                    reconciliationInvoice?.advance_paid,
                    invoice?.advance_paid,
                    invoice?.advance_payment,
                    invoice?.advance_amount,
                    paymentAdvice?.advance_paid,
                    paymentAdvice?.advance_payment,
                    paymentAdvice?.advance_amount,
                  ),
                  "-",
                  firstDisplayValue(
                    paymentReconciliation?.current_upload_total_amount,
                    paymentAdvice?.advance_paid,
                    paymentAdvice?.advance_payment,
                    paymentAdvice?.advance_amount,
                  )
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Expected Balance",
                  "-",
                  firstDisplayValue(
                    reconciliationInvoice?.expected_balance,
                    invoice?.balance_due,
                    invoice?.balance_amount,
                  ),
                  "-",
                  firstDisplayValue(
                    paymentReconciliation?.remaining_advance_amount,
                    paymentAdvice?.balance_due,
                    paymentAdvice?.balance_amount,
                  )
                )}
              {hasPaymentAdvice &&
                renderRow(
                  "Invoice Balance",
                  "-",
                  reconciliationInvoice?.invoice_balance,
                  "-",
                  paymentReconciliation?.total_paid_amount,
                )}

              {renderRow(
                "Subtotal",
                formatNumber(getPoTotal(totalKeys.taxExcluded)),
                formatNumber(getInvoiceTotal(totalKeys.taxExcluded)),
                "-",
                paymentAdvice?.amount
              )}
              {renderRow(
                "Total Price (Incl. Tax)",
                formatNumber(getPoTotal(totalKeys.taxIncluded)),
                formatNumber(getInvoiceTotal(totalKeys.taxIncluded)),
                "-",
                paymentAdvice?.amount
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {mismatchReasons.length > 0 && (
          <Box
            sx={{
              m: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            <Typography sx={{ fontFamily: GILROY_BOLD, color: "#991b1b", mb: 0.5 }}>
              Mismatch Reasons
            </Typography>
            {mismatchReasons.map((reason, index) => (
              <Typography key={`${reason}-${index}`} sx={{ fontFamily: GILROY_MEDIUM, color: "#b91c1c", fontSize: "13px", mt: 0.4 }}>
                {index + 1}. {reason}
              </Typography>
            ))}
          </Box>
        )}
      </>)}
      {/* Modal UI */}
      <Dialog
        className={fullPageView ? "matching-detail-view" : undefined}
        open={Boolean(open) || showModal}
        onClose={closeDialog}
        fullScreen={fullPageView}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          style: {
            borderRadius: fullPageView ? 0 : "8px",
            overflow: "hidden",
            backgroundColor: "#f5f6fb",
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            backgroundColor: fullPageView ? "#f5f6fb" : "#1976d2",
            borderBottom: fullPageView ? "1px solid #e2e8f0" : "none",
            color: fullPageView ? "#0f172a" : "white",
            padding: fullPageView ? "16px 20px 12px" : "12px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", minWidth: 0, gap: "10px" }}>
            <AssignmentIcon
              style={{
                color: fullPageView ? "#6b7280" : "inherit",
                fontSize: fullPageView ? 24 : 28,
                flex: "0 0 auto",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                style={{
                  fontWeight: 600,
                  fontFamily: GILROY_BOLD,
                  fontSize: fullPageView ? "20px" : "18px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Account Payable Automation Comparison
              </Typography>
              {fullPageView && (
                <Typography
                  sx={{
                    color: "#64748b",
                    fontFamily: GILROY_MEDIUM,
                    fontSize: "14px",
                    lineHeight: 1.4,
                    mt: 0.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  TWM ID: {fullPageSummaryItems[0].value} | Matching Type: {fullPageSummaryItems[1].value}
                </Typography>
              )}
            </Box>
          </div>
          {fullPageView && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<ArrowBackIcon />}
              onClick={closeDialog}
              sx={{
                flexShrink: 0,
                minWidth: 88,
                height: 40,
                px: 2,
                borderRadius: "8px",
                backgroundColor: "#2563eb",
                fontFamily: GILROY_BOLD,
                fontSize: "14px",
                lineHeight: 1,
                textTransform: "none",
                "& .MuiButton-startIcon": { display: "flex", mr: 1, ml: 0 },
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              Back
            </Button>
          )}
        </DialogTitle>

        <DialogContent
          style={{
            padding: fullPageView ? "10px 20px 18px" : 0,
            overflow: fullPageView ? "auto" : "hidden",
            backgroundColor: fullPageView ? "#f5f6fb" : "#f8fafc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: fullPageView ? "none" : "calc(100vh - 210px)",
              gap: fullPageView ? 2.5 : 0,
            }}
          >
          {renderFullPageSummary()}
          <TableContainer
            component={Paper}
            style={{
              maxHeight: fullPageView ? "calc(100vh - 420px)" : 500,
              borderRadius: 0,
              border: fullPageView ? "1px solid #cfd5df" : "none",
              overflow: "auto",
              boxShadow: "none",
            }}
          >
            <Table
              sx={{
                minWidth: 1830,
                // tableLayout: "fixed",
                borderRight: "1px solid #edeff3ff",
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      left: 0,
                      zIndex: 6,
                      width: stickySerialWidth,
                      minWidth: stickySerialWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      left: stickySerialWidth,
                      zIndex: 5,
                      width: stickyDescriptionWidth,
                      minWidth: stickyDescriptionWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    Item Description
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>Part No</TableCell>
                  <TableCell align="center" style={headerCellStyle}>Item Code</TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    PO Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Invoice Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    MRN Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (Invoice)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (Invoice)
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    TAX %
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CompareArrowsIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Variance
                      <Tooltip
                        title="PO vs MRN vs Invoice check first, then Invoice vs MRN, else PO vs Invoice"
                        arrow
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Price Variance
                      <Tooltip
                        title="PO vs Invoice Price Variance"
                        arrow
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  {/* New Status Columns */}
                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <InventoryIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Item Status
                      <Tooltip
                        title="Item matching status (PO vs GRN vs Invoice)"
                        arrow
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Status
                      <Tooltip
                        title="Quantity matching status"
                        arrow
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon style={{ fontSize: 16, marginRight: "4px" }} />
                      Price Status
                      <Tooltip
                        title="Price matching status"
                        arrow
                        slotProps={{
                          tooltip: { sx: { fontSize: fullPageView ? "14px" : "12px" } },
                        }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      right: 0,
                      zIndex: 6,
                      width: stickyOverallMatchWidth,
                      minWidth: stickyOverallMatchWidth,
                      backgroundColor: stickyColumnHeaderBg,
                      borderLeft: "1px solid #e2e8f0",
                      boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                    }}
                  >
                    Line Match Status
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modalData.map((row, idx) => {
                  const rowBackground =
                    idx % 2 === 0 ? "rgba(248,250,252,0.9)" : "#ffffff";
                  const stickyBodyBackground =
                    idx % 2 === 0 ? stickyColumnBodyBg : "#ffffff";
                  const overallMatchStatus = getOverallMatchStatus(row);

                  return (
                  <TableRow
                    key={idx}
                    sx={{
                      "& td": {
                        py: 1.8,
                        px: 1.5,
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: GILROY_MEDIUM,
                        fontSize: "14px",
                      },
                      "&:hover td": {
                        backgroundColor: "#eff6ff",
                      },
                    }}
                    style={{
                      backgroundColor: rowBackground,
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_BOLD,
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        zIndex: 4,
                        width: stickySerialWidth,
                        minWidth: stickySerialWidth,
                        backgroundColor: stickyBodyBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_MEDIUM,
                        color: "#475569",
                        fontWeight: 500,
                        position: "sticky",
                        left: stickySerialWidth,
                        zIndex: 3,
                        width: stickyDescriptionWidth,
                        minWidth: stickyDescriptionWidth,
                        backgroundColor: stickyBodyBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {renderDescriptionCell(row.item, idx)}
                    </TableCell>
                    <TableCell style={cellStyle}>{row.partNo || row.part_number||"-"}</TableCell>
                    <TableCell style={cellStyle}>
                      {row.itemCode || "-"}
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.poQty}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#e3f2fd",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.invoiceQty}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#e8f5e9",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.grnQty ?? row.quantity_received}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_BOLD,
                          backgroundColor: "#f3e5f5",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPricePO)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#bbdefb",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPriceInvoice)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPricePO)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPriceInvoice)}
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          row.taxRate
                            ? Number.isFinite(Number(row.taxRate))
                              ? Math.floor(Number(row.taxRate))
                              : row.taxRate
                            : poData?.[0]?.line_items?.[0]?.igst_rate
                            ? Math.floor(Number(poData[0].line_items[0].igst_rate))
                            : invoiceData?.[0]?.line_items?.[0]?.gst_rate
                            ? Math.floor(Number(invoiceData[0].line_items[0].gst_rate))
                            : "-"
                        }
                    
                        style={{
                          fontSize: "14px",
                          fontFamily: GILROY_MEDIUM,
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "quantity")}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.quantityVariance)}
                            variant="outlined"
                            style={{
                              fontSize: "14px",
                              fontFamily: GILROY_MEDIUM,
                              fontWeight: 600,
                              borderColor:
                                row.quantityVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              color:
                                row.quantityVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "price")}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.priceVariance, "price")}
                            variant="outlined"
                            style={{
                              fontSize: "14px",
                              fontFamily: GILROY_MEDIUM,
                              fontWeight: 600,
                              borderColor:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              color:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={
                          row.backendReasons?.length
                            ? row.backendReasons.join(", ")
                            : "Item matching status"
                        }
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.itemStatus.status}
                            type={row.itemStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Quantity status: PO: ${row.poQty}, Invoice: ${row.invoiceQty}, MRN: ${row.grnQty}`}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.quantityStatus.status}
                            type={row.quantityStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Price status: PO: ${row.unitPricePO}, Invoice: ${row.unitPriceInvoice}`}
                        slotProps={{ tooltip: { sx: { fontSize: fullPageView ? "14px" : "13px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.priceStatus.status}
                            type={row.priceStatus.type}
                            fontWeight={400}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        right: 0,
                        zIndex: 5,
                        width: stickyOverallMatchWidth,
                        minWidth: stickyOverallMatchWidth,
                        backgroundColor: stickyBodyBackground,
                        borderLeft: "1px solid #e2e8f0",
                        boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                      }}
                    >
                      <ModalStatusChip
                        status={overallMatchStatus.status}
                        type={overallMatchStatus.type}
                        fontWeight={400}
                      />
                    </TableCell>
                  </TableRow>
                  );
                })}

                {modalData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={18}
                      align="center"
                      style={{ padding: "32px 16px" }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
            <Box
              sx={{
                flexShrink: 0,
                borderTop: fullPageView ? 0 : "1px solid #e2e8f0",
                backgroundColor: fullPageView ? "transparent" : "#ffffff",
                px: fullPageView ? 0 : 1.5,
                py: fullPageView ? 0 : 1,
              }}
            >
              <Box
                className="matching-view-footer"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "minmax(300px, 0.7fr) minmax(720px, 1.3fr)",
                  },
                  gap: fullPageView ? 1.5 : 1,
                  alignItems: "stretch",
                }}
              >
                <Box
                  className="matching-view-card matching-view-shipping"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.8,
                    px: fullPageView ? 1.5 : 1,
                    py: fullPageView ? 1.4 : 0.85,
                    borderRadius: fullPageView ? 0 : 1.5,
                    backgroundColor: fullPageView ? "#eef4ff" : "#f0f7ff",
                    border: fullPageView ? "1px solid #c8d2e3" : "1px solid #dbeafe",
                    alignSelf: "stretch",
                    minHeight: fullPageView ? 100 : "auto",
                  }}
                >
                  <ShippingIcon
                    style={{
                      color: "#1976d2",
                      marginTop: "2px",
                      fontSize: "18px",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: "#1976d2",
                        mb: fullPageView ? 1.4 : "2px",
                        fontFamily: GILROY_BOLD,
                        borderBottom: fullPageView ? "1px solid #c8d2e3" : "none",
                        pb: fullPageView ? 0.8 : 0,
                        fontSize: fullPageView ? "16px" : "14px",
                      }}
                    >
                      Shipping Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: fullPageView ? "15px" : "12px",
                        fontWeight: 700,
                        fontFamily: GILROY_BOLD,
                        lineHeight: 1.3,
                      }}
                    >
                      {addressLines[0]}
                    </Typography>
                    {addressLines.slice(1).map((line, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          fontSize: fullPageView ? "14px" : "11px",
                          fontWeight: 400,
                          fontFamily: GILROY_MEDIUM,
                          lineHeight: 1.3,
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box
                  className="matching-view-card matching-view-amounts"
                  sx={{
                    px: fullPageView ? 1.5 : 1,
                    py: fullPageView ? 1.4 : 0.85,
                    borderRadius: fullPageView ? 0 : 1.5,
                    background: fullPageView
                      ? "linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)"
                      : "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)",
                    border: fullPageView ? "1px solid #cbd5e1" : "1px solid #dbeafe",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: fullPageView ? 1 : 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: fullPageView ? "16px" : "14px",
                      fontFamily: GILROY_BOLD,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: fullPageView ? "#0f172a" : "#166534",
                      borderBottom: fullPageView ? "1px solid #cbd5e1" : "none",
                      pb: fullPageView ? 1 : 0,
                    }}
                  >
                    Amount Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        xl: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: fullPageView ? 0.5 : 0.75,
                    }}
                  >
                    {renderTotalTile(
                      "PO Subtotal",
                      getPoTotal(totalKeys.taxExcluded)
                    )}
                    {renderTotalTile(
                      "Invoice Subtotal",
                      getInvoiceTotal(totalKeys.taxExcluded)
                    )}
                    {renderTotalTile(
                      "PO Total (Incl. Tax)",
                      getPoTotal(totalKeys.taxIncluded)
                    )}
                    {renderTotalTile(
                      "Invoice Total (Incl. Tax)",
                      getInvoiceTotal(totalKeys.taxIncluded)
                    )}
                  </Box>
                </Box>
                {hasPaymentAdvice && (
                  <Box
                    className="matching-view-card matching-view-payment"
                    sx={{
                      gridColumn: "1 / -1",
                      px: fullPageView ? 1.5 : 1,
                      py: fullPageView ? 1.4 : 1.25,
                      borderRadius: fullPageView ? 0 : 2,
                      background: fullPageView
                        ? "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)"
                        : "linear-gradient(135deg, #eff6ff 0%, #f8fafc 58%, #ecfdf5 100%)",
                      border: fullPageView ? "1px solid #cbd5e1" : "1px solid #bfdbfe",
                      boxShadow: fullPageView ? "none" : "0 10px 24px rgba(15, 23, 42, 0.08)",
                      display: "flex",
                      flexDirection: "column",
                      gap: fullPageView ? 1 : 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: fullPageView ? "16px" : "14px",
                        fontFamily: GILROY_BOLD,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: fullPageView ? "#0f172a" : "#0f4c81",
                        borderBottom: "1px solid #cbd5e1",
                        pb: 0.9,
                      }}
                    >
                      Payment Details
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(5, minmax(0, 1fr))",
                        },
                        gap: fullPageView ? 0.5 : 0.75,
                      }}
                    >
                      {renderPaymentDetail(
                        "Payment Advice No",
                        paymentAdvice?.payment_advice_number
                      )}
                    
                      {renderPaymentDetail(
                        "Transaction Date",
                        paymentAdvice?.transaction_date
                      )}
                      {renderPaymentDetail(
                        "Payment Mode",
                        paymentAdvice?.payment_product
                      )}
                      {renderPaymentDetail(
                        "UTR / RRN",
                        paymentAdvice?.utr_rrn_no
                      )}
                      {renderPaymentDetail(
                        "Advice Paid",
                        paymentReconciliation?.current_upload_total_amount ??
                          paymentReconciliation?.payment_advice?.paid_amount ??
                          paymentAdvice?.amount,
                      )}
                      {renderPaymentDetail(
                        "Balance Due",
                        reconciliationInvoice?.expected_balance ??
                          invoice?.balance_amount_payable ??
                          invoice?.balance_due,
                      )}
                      {renderPaymentDetail(
                        "Invoice Amount",
                        reconciliationInvoice?.invoice_total ??
                          invoice?.invoice_amount ??
                          invoice?.total_amount,
                      )}
                    
                    </Box>
                    {mismatchReasons.length > 0 && (
                      <Box sx={{ p: 1.25, borderRadius: 1.5, background: "linear-gradient(135deg, #fff1f2 0%, #fef2f2 100%)", border: "1px solid #fca5a5", boxShadow: "inset 4px 0 0 #dc2626" }}>
                        <Typography sx={{ fontFamily: GILROY_BOLD, color: "#991b1b", mb: 0.5 }}>Remarks</Typography>
                        {mismatchReasons.map((reason, index) => (
                          <Typography key={`${reason}-${index}`} sx={{ fontFamily: GILROY_MEDIUM, color: "#b91c1c", fontSize: "13px", mt: 0.55, lineHeight: 1.45 }}>
                            {index + 1}. {reason}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {!fullPageView && (
          <div
            style={{
              padding: "12px 24px",
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Button
              variant="outlined"
              onClick={closeDialog}
              style={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: GILROY_MEDIUM,
              }}
            >
              Close
            </Button>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ThreeWayMatchTable;
